// TODO: constants should be split between features
import COLORS from '@zeus/core/colors';
import { DestinationPushStatus } from '_/features/destinationPushes/common/constants';
import { SnapshotStatus } from '_/features/snapshots/common/snapshot.constants';
import { VoidHandler } from '_/types/common.types';
import { Tag } from 'antd';
import React, { ReactElement } from 'react';

export const emptyFn: VoidHandler = () => {
  // empty
};

export const nameMaxLength = 128;

export const nameMaxLengthErrorMessage = `Name mustn\'t exceed ${nameMaxLength} characters`;

const parameterPatternText = '^[a-z]([a-z0-9]|_[a-z0-9])*$';

export const parameterPattern = new RegExp(parameterPatternText);

export const DEFAULT_README = `## README 
-----
#### This is where you put all the information you want to share`;
export const DEFAULT_ALARM_README = 'Use this to add information about the alarm to be helpful in runbook';
export const SOURCE_STATUS_OPTIONS = [
  'QUEUED',
  'IN_PROGRESS',
  'QA',
  'HOLD',
  'ISSUE',
  'BLOCKING',
  'READY',
  'ACTIVE',
  'MAINTENANCE',
];

export const COLLECTION_ACTIVITY_OPTIONS = ['History'];

export const SCHEMA_ACTIVITY_OPTIONS = ['History'];

export const SOURCE_ACTIVITY_OPTIONS = ['Comments', 'History', 'Transitions'];

export const DELIVERY_ACTIVITY_OPTIONS = ['Overview', 'Transitions', 'Comments'];

export const PROJECT_ENVIRONMENT_OPTIONS = ['DEV', 'STAGING', 'PRODUCTION'];

export const PROJECT_FLOW_TYPES = ['LEGACY', 'SIMPLE', 'CHAINED'];

export const ALARM_INTEGRATION_TYPES = ['EMAIL', 'PAGERDUTY'];

export const ALARM_COMPARISON_OPERATORS = [
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUAL_TO',
  'LESS_THAN',
  'LESS_THAN_OR_EQUAL_TO',
  'NOT_EQUAL_TO',
];

export const SOURCE_STATUS_COLOR_MAPPING = {
  QUEUED: COLORS.PURPLE,
  IN_PROGRESS: COLORS.PURPLE,
  QA: COLORS.YELLOW,
  HOLD: COLORS.MEDIUM_GREY,
  ISSUE: COLORS.RED,
  BLOCKING: COLORS.DARKER_RED,
  READY: COLORS.GREEN,
  ACTIVE: COLORS.GREEN,
  MAINTENANCE: COLORS.YELLOW,
};

export const PUSH_STATUS_COLOR_MAPPING = {
  REVERTED: COLORS.PURPLE,
  IN_PROGRESS: COLORS.PURPLE,
  FAILED: COLORS.RED,
  PENDING: COLORS.YELLOW,
  REVERTING: COLORS.MEDIUM_GREY,
  INTERNAL_FAILURE: COLORS.DARKER_RED,
  COMPLETE: COLORS.GREEN,
  REVERT_FAILED: COLORS.DARKER_RED,
};

export const setPushStatusTag = (status: string): ReactElement => {
  return <Tag color={PUSH_STATUS_COLOR_MAPPING[status]}>{status}</Tag>;
};

export const ENVIRONMENT_COLOR_MAPPING = {
  DEV: COLORS.MEDIUM_GREY,
  STAGING: COLORS.MEDIUM_BLUE,
  PRODUCTION: COLORS.GREEN,
};

export const setEnvironmentTag = (environment: string): ReactElement => {
  return <Tag color={ENVIRONMENT_COLOR_MAPPING[environment]}>{environment}</Tag>;
};

export const ALARM_SEVERITY = {
  CRITICAL: 'CRITICAL',
  ERROR: 'ERROR',
  WARNING: 'WARNING',
  INFO: 'INFO',
};

export const ALARM_METRIC_OPTIONS = {
  health: {
    blockedPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    collectedByPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    dataPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    dupePct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    errorPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    failedOptionalTests: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    failedRequiredTests: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    filteredPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    noData200Pct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    noDataPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    noHtmlPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    noScreenshotPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    notFoundPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    okPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    pages: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    qualityScore: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    rows: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    rowsPer200: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    rowsPerPage: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    verrorsP50: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    verrorsP75: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    verrorsP95: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    verrorsP99: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    timedOutPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    transformFailurePct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputBlockedPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputCollectedByPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputDataPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputErrorPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputNoData200Pct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputNoDataPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputNoHtmlPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputNoScreenshotPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputNotFoundPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputOkPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputRowsPer200: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputTimedOutPct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputTransformFailurePct: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    inputs: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    rowsPerInput: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    pagesPerInput: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
    pagesPerInput200: ['sum', 'min', 'avg', 'weightedAvg', 'snapshots', 'inputs'],
  },
  status: SnapshotStatus,
  progress: {
    processed: 'processed',
    total: 'total',
    collected: 'collected',
    shouldHaveBeenCollected: 'shouldHaveBeenCollected',
    expectedPct: 'expectedPct',
  },
  dupes: 'dupes',
  pushes: DestinationPushStatus,
  timeliness: {
    pushed: 'pushed',
    collectedOnTime: 'collectedOnTime',
    pushedOnTime: 'pushedOnTime',
    shouldHaveBeenCollected: 'shouldHaveBeenCollected',
    shouldHaveBeenPushed: 'shouldHaveBeenPushed',
    pushedOnTimePct: 'pushedOnTimePct',
    collectedOnTimePct: 'collectedOnTimePct',
  },
};

export const DeliveryStatus = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  ERROR: 'ERROR',
  PENDING: 'PENDING',
  PENDING_QUEUE: 'PENDING_QUEUE',
};

export const COLLECTION_CHECKS = {
  VALIDATION_ERRORS: 'VALIDATION_ERRORS',
  FALSE_VALUES: 'FALSE_VALUES',
  RECORDS: 'RECORDS',
  MISSING_VALUES: 'MISSING_VALUES',
  CUSTOM: 'CUSTOM',
  OBJECT_SELECT: 'OBJECT_SELECT',
  HEALTH_NUMBER: 'HEALTH_NUMBER',
  METRIC_NUMBER: 'METRIC_NUMBER',
  VALUES: 'VALUES',
};

export const COLLECTION_CHECKS_COLOR_MAPPING = {
  VALIDATION_ERRORS: COLORS.RED,
  FALSE_VALUES: COLORS.PURPLE,
  RECORDS: COLORS.CYAN,
  MISSING_VALUES: COLORS.ORANGE,
  CUSTOM: COLORS.GREEN,
  OBJECT_SELECT: COLORS.SALMON,
  HEALTH_NUMBER: COLORS.YELLOW,
  METRIC_NUMBER: COLORS.DARKER_RED,
  VALUES: COLORS.DARK_CYAN,
};

export const FLOW_CONFIGURATION_S3_EXTENSIONS_DEFAULT = '.json';

export const FLOW_CONFIGURATION_S3_EXTENSIONS = [FLOW_CONFIGURATION_S3_EXTENSIONS_DEFAULT, '.csv'];

export const templateVariables = (type: string): ReactElement => {
  const start_YYYY = '{start_YYYY}';
  let temp;

  switch (type) {
    case 's3':
      temp =
        ':org, :project, :source, :source.parameter (ie. :source.domain), :collection, :snapshot_id, :crawl_run_id, :delivery_id, :flow, :rows, :ext. (inferred extension)';
      break;
    case 'flow':
      temp = ':org, :project, :source, :flow';
      break;
    default:
      temp = null;
  }

  return (
    <div>
      <h3>Available template variables: </h3>
      <p>{temp}</p>
      <h3>Available date/time formats: </h3>
      <p>
        ['YYYY', 'YY', 'M', 'MM', 'W', 'WW', 'D', 'DD', 'd', 'H', 'HH', 'm', 'mm', 's', 'ss'] <br />
        Also ‘w' and 'ww’ for a week that starts on Sunday instead of Monday
      </p>
      {type === 's3' && (
        <div>
          <h3>Available prefixes: </h3>
          <p>
            :scheduled_, :start_, :end_, :deliver_by_, :collect_by_, :must_finish_by_, :stop_by_, :delivery_start_,
            :delivery_end_, :delivery_deliver_by_, :delivery_collect_by_
          </p>
          <p>Variables can be surrounded by curly braces, e.g. :{start_YYYY}</p>
        </div>
      )}
    </div>
  );
};

export const getSQLFromURLEncodedString = (where: string, obj: Record<string, unknown>): string => {
  // Replace sprintf with simple string replacement for %(key)s patterns
  const whereClause = where.replace(/%\((\w+)\)s/g, (_, key) => String(obj[key] ?? ''));
  const sql = encodeURIComponent(`SELECT * FROM S3Object s WHERE ${whereClause} LIMIT 200`);

  const uriEncodedString = `${encodeURIComponent(whereClause)}&sql=${sql}`;
  return uriEncodedString;
};
